import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getPaymentMethod } from "../../core/api/Api";
import useLangNavigate from "../common/useLangNavigate";
import Loading from "../common/loading/Loading";
import "./PaymentMethods.css";

const BRAND_ICONS = {
  VISA: "bi-credit-card-2-front",
  MASTERCARD: "bi-credit-card-2-back",
  AMEX: "bi-credit-card",
  CB: "bi-credit-card-fill",
};

const PaymentMethods = () => {
  const { t } = useTranslation();
  const navigate = useLangNavigate();
  const mounted = useRef(true);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPaymentMethod()
      .then((res) => {
        if (!mounted.current) return;
        setPaymentMethod(res?.data || null);
        setLoading(false);
      })
      .catch(() => {
        if (!mounted.current) return;
        setPaymentMethod(null);
        setLoading(false);
      });

    return () => {
      mounted.current = false;
    };
  }, []);

  if (loading) return <Loading />;

  const hasCard = paymentMethod?.has_card;
  const hasFailure = !!paymentMethod?.payment_failed_at;
  const isPaused = !!paymentMethod?.is_paused;
  const brandKey = (paymentMethod?.card_brand || "").toUpperCase();
  const iconClass = BRAND_ICONS[brandKey] || "bi-credit-card";

  let statusKey = "subscribe.status_active";
  let statusClass = "status-active";
  if (isPaused) {
    statusKey = "subscribe.status_cancelled";
    statusClass = "status-cancelled";
  } else if (hasFailure) {
    statusKey = "subscribe.status_payment_failed";
    statusClass = "status-failed";
  }

  return (
    <section className="payment-methods-section py-100">
      <div className="container">
        <div className="payment-methods-card">
          <h2 className="payment-methods-title">{t("payment_methods")}</h2>

          {(hasCard || isPaused || hasFailure) ? (
            <>
              <div className={`status-banner ${statusClass}`}>
                {t(statusKey)}
              </div>

              {(hasFailure || isPaused) && (
                <div className="failure-callout">
                  <p>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {isPaused
                      ? t("subscribe.payment_failed_paused_message")
                      : t("subscribe.payment_failed_message")}
                  </p>
                </div>
              )}

              {hasCard && (
                <div className="card-display">
                  <div className="card-brand">
                    <i className={`bi ${iconClass}`}></i>
                    <span>{paymentMethod.card_brand || ""}</span>
                  </div>
                  {paymentMethod.card_last4 === "xxxx" ? (
                    <div className="card-number">
                      <span>{t("subscribe.card_on_file")}</span>
                    </div>
                  ) : (
                    <div className="card-number">
                      <span>••••</span>
                      <span>••••</span>
                      <span>••••</span>
                      <span>{paymentMethod.card_last4}</span>
                    </div>
                  )}
                </div>
              )}

              <button
                className="change-card-btn"
                onClick={() => navigate("/update-payment-method")}
              >
                <i className="bi bi-pencil me-2"></i>
                {hasFailure || isPaused
                  ? t("subscribe.update_payment_method")
                  : t("subscribe.change_payment_method")}
              </button>

              <div className="manage-link">
                <button
                  className="link-btn"
                  onClick={() => navigate("/manage-subscriptions")}
                >
                  {t("subscribe.view_subscription_details")} →
                </button>
              </div>
            </>
          ) : (
            <div className="no-card-state">
              <i className="bi bi-credit-card no-card-icon"></i>
              <p>{t("subscribe.no_card_on_file")}</p>
              <button
                className="subscribe-cta-btn"
                onClick={() => navigate("/manage-subscriptions")}
              >
                {t("subscribe.subscribe")}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PaymentMethods;
